import React, { useState } from 'react';
import { Box, Dialog, Divider, MenuItem } from '@mui/material';
import Text from '../../../components/UI/atoms/Text';
import Button from '../../../components/UI/atoms/Button';
import Card from '../../../components/UI/molecules/Card';
import Input from '../../../components/UI/atoms/Input';
import { authPost } from '../../../services/authCalls';
import { useApp } from '../../../AppContext';

const SHIPPING_OPTIONS = ['Digital', 'Print', 'Both'];
const RENEWAL_TYPES = ['Monthly', 'Annual'];

const emptyForm = {
  planKey: '',
  productId: '',
  priceId: '',
  subscriptionTag: '',
  renewalType: 'Monthly',
  seatCount: 1,
  shippingPreference: 'Digital',
  renewalDate: '',
  subscriptionEndDate: '',
  subscriptionStartDate: '',
};

export default function CreateManualSubModal({ open, onClose, firmId, plans, onSuccess }) {
  const { handleMessage } = useApp();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handlePlanSelect = (planKey) => {
    const plan = plans.find((_, i) => String(i) === planKey);
    if (!plan) { set('planKey', ''); return; }
    setForm(prev => ({
      ...prev,
      planKey,
      productId: plan.ProductId || plan.productId || '',
      priceId: plan.PriceId || plan.priceId || '',
      subscriptionTag: plan.SubscriptionTag || plan.subscriptionTag || '',
      renewalType: plan.RenewalType || plan.renewalType || 'Monthly',
    }));
  };

  const handleClose = () => {
    setForm(emptyForm);
    onClose();
  };

  const save = () => {
    if (!form.productId || !form.priceId) {
      handleMessage('error', 'Select a plan first');
      return;
    }
    setSaving(true);
    authPost('/api/admin/subscription/manual/insert', {
      firmId,
      priceId: form.priceId,
      productId: form.productId,
      renewalDate: form.renewalDate ? new Date(form.renewalDate) : null,
      renewalType: form.renewalType,
      subscriptionStartDate: form.subscriptionStartDate ? new Date(form.subscriptionStartDate) : null,
      subscriptionEndDate: form.subscriptionEndDate ? new Date(form.subscriptionEndDate) : null,
      subscriptionTag: form.subscriptionTag,
      seatCount: Number(form.seatCount) || 1,
      shippingPreference: form.shippingPreference,
    })
      .then(() => {
        handleMessage('success', 'Manual subscription created');
        handleClose();
        onSuccess && onSuccess();
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Failed to create subscription';
        handleMessage('error', msg);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Dialog onClose={handleClose} open={open}>
      <Card sx={{ margin: 0, width: { xs: '340px', sm: '580px' } }}>
        <Text type='h1' sx={{ mb: '12px' }}>Create Manual Subscription</Text>
        <Divider sx={{ mb: '20px' }} />

        {/* Plan selector */}
        <Input
          label='Select Plan'
          id='planKey'
          select
          value={form.planKey}
          onChange={e => handlePlanSelect(e.target.value)}
        >
          <MenuItem value=''>— choose a plan —</MenuItem>
          {(plans || []).map((p, i) => (
            <MenuItem key={i} value={String(i)}>
              {p.ProductName || p.productName} &nbsp;·&nbsp; {p.SubscriptionTag || p.subscriptionTag} &nbsp;·&nbsp; {p.RenewalType || p.renewalType}
            </MenuItem>
          ))}
        </Input>

        {/* Show selected IDs as read-only confirmation */}
        {form.productId && (
          <Box sx={{ display: 'flex', gap: '12px', mb: '4px' }}>
            <Box sx={{ flex: 1 }}>
              <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', mb: '2px' }}>Product ID</Text>
              <Text sx={{ fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all' }}>{form.productId}</Text>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', mb: '2px' }}>Price ID</Text>
              <Text sx={{ fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all' }}>{form.priceId}</Text>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: '12px', mt: '8px' }}>
          <Box sx={{ flex: 1 }}>
            <Input
              label='Renewal Type'
              id='renewalType'
              select
              value={form.renewalType}
              onChange={e => set('renewalType', e.target.value)}
            >
              {RENEWAL_TYPES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Input>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Input
              label='Shipping Preference'
              id='shippingPreference'
              select
              value={form.shippingPreference}
              onChange={e => set('shippingPreference', e.target.value)}
            >
              {SHIPPING_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Input>
          </Box>
        </Box>

        <Input
          label='Seat Count'
          id='seatCount'
          type='number'
          value={form.seatCount}
          onChange={e => set('seatCount', e.target.value)}
        />

        <Box sx={{ display: 'flex', gap: '12px' }}>
          <Box sx={{ flex: 1 }}>
            <Input
              label='Start Date'
              id='subscriptionStartDate'
              type='date'
              value={form.subscriptionStartDate}
              onChange={e => set('subscriptionStartDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Input
              label='End Date'
              id='subscriptionEndDate'
              type='date'
              value={form.subscriptionEndDate}
              onChange={e => set('subscriptionEndDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        <Input
          label='Renewal Date'
          id='renewalDate'
          type='date'
          value={form.renewalDate}
          onChange={e => set('renewalDate', e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', mt: '20px' }}>
          <Button type='outlined' onClick={handleClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Creating…' : 'Create Subscription'}
          </Button>
        </Box>
      </Card>
    </Dialog>
  );
}
